import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../api/client';
import './Settings.css';

const TABS = ['Profile','Company','Security','Backup','Notifications','Billing','Roles'];

export default function Settings() {
  const { theme }  = useTheme();
  const { user }   = useAuth();
  const { company, update } = useCompany();
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get('tab');
  const [tab, setTab] = useState(urlTab || 'Profile');
  useEffect(() => { if (urlTab) setTab(urlTab); }, [urlTab]);
  const [saved,  setSaved]  = useState(false);
  const [co,     setCo]     = useState(() => {
    try {
      const profile = JSON.parse(localStorage.getItem('ryzenos_company_profile') || 'null');
      const onboarding = JSON.parse(localStorage.getItem('ryzenos_company') || 'null');
      return profile || onboarding || company;
    } catch { return company; }
  });

  /* Profile state -- controlled inputs */
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ryzenos_user_profile') || 'null') || {name:user.name||'',email:user.email||'',phone:'',dept:user.dept||''}; } catch { return {name:user.name||'',email:user.email||'',phone:'',dept:user.dept||''}; }
  });
  const setP = (k,v) => setProfile(p=>({...p,[k]:v}));
  const setCof = (k,v) => setCo(p=>({...p,[k]:v}));

  /* Security state */
  const [sec, setSec] = useState({cur:'',newPw:'',confirm:''});
  const [secMsg, setSecMsg] = useState('');
  const setS = (k,v) => setSec(p=>({...p,[k]:v}));

  const showSaved = () => { setSaved(true); setTimeout(()=>setSaved(false),2000); };

  const saveProfile = () => {
    localStorage.setItem('ryzenos_user_profile', JSON.stringify(profile));
    showSaved();
  };

  const saveCompany = () => {
    update(co);
    localStorage.setItem('ryzenos_company_profile', JSON.stringify(co));
    showSaved();
  };

  const updatePassword = () => {
    if (!sec.cur) { setSecMsg('Enter your current password'); return; }
    if (sec.newPw.length < 6) { setSecMsg('New password must be at least 6 characters'); return; }
    if (sec.newPw !== sec.confirm) { setSecMsg('Passwords do not match'); return; }
    api.auth.changePassword(sec.newPw)
      .then(() => {
        setSecMsg('Password updated successfully');
        setSec({cur:'',newPw:'',confirm:''});
        setTimeout(()=>setSecMsg(''),3000);
      })
      .catch(err => setSecMsg(err.message || 'Failed to update password'));
  };

  /* Backup state */
  const [backupMsg, setBackupMsg] = useState('');
  const downloadBackup = () => {
    setBackupMsg('Preparing backup...');
    fetch(api.backup.exportUrl(), {
      headers: { Authorization: `Bearer ${api.getToken()}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Backup failed');
        return res.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ryzenos-backup-${new Date().toISOString().slice(0,10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setBackupMsg('Backup downloaded.');
        setTimeout(()=>setBackupMsg(''), 3000);
      })
      .catch(() => {
        setBackupMsg('Backup failed. Try again.');
        setTimeout(()=>setBackupMsg(''), 3000);
      });
  };

  return (
    <div className="st-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="st-main">
        <Topbar title="Settings" subtitle="Manage your account and workspace"/>
        <div className="st-body">
          <div className="st-nav">
            {TABS.filter(t => t !== 'Backup' || user.role === 'Super Admin' || user.role === 'Admin').map(t=><div key={t} className={`st-nav-item${tab===t?' active':''}`} onClick={()=>setTab(t)}>{t}</div>)}
          </div>
          <div className="st-content">

            {tab==='Profile' && (
              <div className="st-section">
                <div className="st-sec-title">My Profile</div>
                <div className="st-av-row">
                  <div className="st-av">{user.initials}</div>
                  <div>
                    <div className="st-av-name">{profile.name||user.name}</div>
                    <div className="st-av-role">{user.role}</div>
                  </div>
                </div>
                <div className="st-fields">
                  <div className="st-field-grp"><label className="st-lbl">Name</label><input className="st-inp" value={profile.name} onChange={e=>setP('name',e.target.value)}/></div>
                  <div className="st-field-grp"><label className="st-lbl">Email</label><input className="st-inp" value={profile.email} onChange={e=>setP('email',e.target.value)}/></div>
                  <div className="st-field-grp"><label className="st-lbl">Phone</label><input className="st-inp" value={profile.phone} placeholder="+91 XXXXX XXXXX" onChange={e=>setP('phone',e.target.value)}/></div>
                  <div className="st-field-grp"><label className="st-lbl">Department</label><input className="st-inp" value={profile.dept} onChange={e=>setP('dept',e.target.value)}/></div>
                </div>
                <div className="st-save-row">
                  <button className="st-save-btn" onClick={saveProfile}>{saved?'✓ Saved':'Save changes'}</button>
                </div>
              </div>
            )}

            {tab==='Company' && (
              <div className="st-section">
                <div className="st-sec-title">Company Profile</div>
                <div className="st-fields">
                  {[
                    {k:'name',    label:'Company name',   placeholder:'Ryzenforge Enterprises LLP'},
                    {k:'tagline', label:'Tagline',         placeholder:'Run your business smarter'},
                    {k:'email',   label:'Business email',  placeholder:'connect@ryzenforge.com'},
                    {k:'phone',   label:'Phone',           placeholder:'+91 81295 82113'},
                    {k:'website', label:'Website',         placeholder:'www.ryzenforge.com'},
                    {k:'gstin',   label:'GSTIN',           placeholder:'22AAAAA0000A1Z5'},
                    {k:'address', label:'Address',         placeholder:'Tower 2, DD Platinum Planet'},
                    {k:'city',    label:'City',            placeholder:'Kochi'},
                    {k:'state',   label:'State',           placeholder:'Kerala'},
                    {k:'pincode', label:'Pincode',         placeholder:'682017'},
                  ].map(f=>(
                    <div key={f.k} className="st-field-grp">
                      <label className="st-lbl">{f.label}</label>
                      <input className="st-inp" placeholder={f.placeholder} value={co[f.k]||''} onChange={e=>setCof(f.k,e.target.value)}/>
                    </div>
                  ))}
                </div>
                <div className="st-save-row">
                  <button className="st-save-btn" onClick={saveCompany}>{saved?'✓ Saved':'Save company profile'}</button>
                </div>
              </div>
            )}

            {tab==='Security' && (
              <div className="st-section">
                <div className="st-sec-title">Security</div>
                <div className="st-fields">
                  <div className="st-field-grp"><label className="st-lbl">Current password</label><input className="st-inp" type="password" value={sec.cur} onChange={e=>setS('cur',e.target.value)} placeholder="••••••••"/></div>
                  <div className="st-field-grp"><label className="st-lbl">New password</label><input className="st-inp" type="password" value={sec.newPw} onChange={e=>setS('newPw',e.target.value)} placeholder="••••••••"/></div>
                  <div className="st-field-grp"><label className="st-lbl">Confirm password</label><input className="st-inp" type="password" value={sec.confirm} onChange={e=>setS('confirm',e.target.value)} placeholder="••••••••"/></div>
                </div>
                {secMsg && <div className={`st-msg ${secMsg.includes('success')?'st-msg-ok':'st-msg-err'}`}>{secMsg}</div>}
                <div className="st-save-row"><button className="st-save-btn" onClick={updatePassword}>Update password</button></div>
              </div>
            )}

            {tab==='Backup' && (
              <div className="st-section">
                <div className="st-sec-title">Backup</div>
                <div className="st-help-text" style={{fontSize:13,marginBottom:16,lineHeight:1.5}}>
                  Download a complete copy of all your CRM data — leads, clients, invoices, expenses, projects, team, calendar events, and notifications — as a JSON file you can store anywhere.
                </div>
                <div className="st-save-row">
                  <button className="st-save-btn" onClick={downloadBackup}>Download Backup Now</button>
                </div>
                {backupMsg && <div className={`st-msg ${backupMsg.includes('downloaded')?'st-msg-ok':backupMsg.includes('failed')?'st-msg-err':''}`} style={{marginTop:10}}>{backupMsg}</div>}

                <div style={{marginTop:28,paddingTop:20,borderTop:'.5px solid rgba(128,128,128,.15)'}}>
                  <div className="st-help-title" style={{fontSize:13,fontWeight:600,marginBottom:6}}>Automatic weekly backups</div>
                  <div className="st-help-text" style={{fontSize:12,lineHeight:1.5}}>
                    The server automatically backs up the entire database every Sunday at 2:00 AM and keeps the last 8 weeks of backups. This runs independently of the app, so your data is protected even if something goes wrong.
                  </div>
                </div>
              </div>
            )}

            {tab==='Notifications' && (
              <div className="st-section">
                <div className="st-sec-title">Notification Preferences</div>
                {[
                  {key:'followup',  label:'Follow-up reminders',   desc:'Get notified when a lead follow-up is due'},
                  {key:'invoice',   label:'Invoice due alerts',     desc:'Get notified when an invoice is overdue'},
                  {key:'team',      label:'Team attendance',        desc:'Daily attendance summary at 10 AM'},
                  {key:'lead',      label:'New lead alerts',        desc:'Notify when a new lead is added'},
                  {key:'task',      label:'Task deadlines',         desc:'Remind 1 day before a task is due'},
                ].map(n => {
                  const stored = JSON.parse(localStorage.getItem('ryzenos_notif_prefs') || '{}');
                  const isOn = stored[n.key] !== false;
                  return (
                    <div key={n.key} className="st-notif-row">
                      <div>
                        <div className="st-notif-lbl">{n.label}</div>
                        <div className="st-notif-desc">{n.desc}</div>
                      </div>
                      <div className={`st-toggle${isOn?' on':''}`} onClick={()=>{
                        const p = JSON.parse(localStorage.getItem('ryzenos_notif_prefs') || '{}');
                        p[n.key] = !isOn;
                        localStorage.setItem('ryzenos_notif_prefs', JSON.stringify(p));
                        showSaved();
                      }}><div className="st-toggle-thumb"/></div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab==='Billing' && (
              <div className="st-section">
                <div className="st-sec-title">Billing</div>
                <div className="st-billing-card">
                  <div className="st-plan-name">RyzenOS Pro</div>
                  <div className="st-plan-price">₹7,499 <span>/month</span></div>
                  <div className="st-plan-status">✓ Active — Next billing 26 June 2026</div>
                </div>
                <div className="st-billing-info">
                  {[{label:'Plan',val:'Pro — Unlimited leads, team, finance'},{label:'Users',val:'Up to 25 team members'},{label:'Storage',val:'50 GB'},{label:'Support',val:'Priority support'}].map(r=>(
                    <div key={r.label} className="st-info-row"><span className="st-info-lbl">{r.label}</span><span className="st-info-val">{r.val}</span></div>
                  ))}
                </div>
                <div className="st-save-row"><button className="st-cancel-btn" onClick={()=>alert('Please contact support@ryzenforge.com to cancel your subscription.')}>Cancel subscription</button></div>
              </div>
            )}

            {tab==='Roles' && (
              <div className="st-section">
                <div className="st-sec-title">Roles & Permissions</div>
                <div className="st-roles-info">Roles are assigned from the Team page. Each role controls what a member can see and do.</div>
                {[
                  {role:'Super Admin',color:'#F5A623',perms:['Full access to all modules','Add/edit/delete leads, clients, team','Manage billing and settings','View all reports and analytics']},
                  {role:'Admin',color:'#3B82F6',perms:['View all modules','Add/edit leads, clients, projects','Cannot delete team members','Cannot access billing']},
                  {role:'User',color:'#22C55E',perms:['Access employee portal only','Mark own attendance','View assigned tasks','Cannot access CRM data']},
                ].map(r=>(
                  <div key={r.role} className="st-role-card">
                    <div className="st-role-hd"><div className="st-role-name" style={{color:r.color}}>{r.role}</div></div>
                    <ul className="st-role-perms">{r.perms.map(p=><li key={p} className="st-role-perm">{p}</li>)}</ul>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
